import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api";
import ProductCard from "@/components/ProductCard";
import { ChevronLeft } from "lucide-react";

export default function PutterSubcategory() {
  const { subSlug } = useParams();
  const [sub, setSub] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSub(null);
    setNotFound(false);
    setLoading(true);
    api.get("/site-settings")
      .then(({ data }) => {
        const match = (data.putter_subcategories || []).find((s) => s.slug === subSlug);
        if (!match) { setNotFound(true); return; }
        setSub(match);
      })
      .catch(() => setNotFound(true));
    api.get("/products", { params: { subcategory_slug: subSlug } })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [subSlug]);

  if (notFound) {
    return (
      <div className="container-ping py-24 text-center">
        <p className="text-muted-foreground">Putter family not found.</p>
        <Link to="/equipment/putters" className="mt-4 inline-block underline">Back to Putters</Link>
      </div>
    );
  }

  return (
    <div className="pb-16" data-testid="putter-subcategory-page">
      {/* Hero */}
      <section className="relative h-[260px] md:h-[340px] w-full overflow-hidden bg-[#0A0A0A]">
        {sub?.image && (
          <img
            src={sub.image}
            alt={sub.name}
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: sub.image_fit || "cover", objectPosition: sub.image_position || "center" }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="relative container-ping h-full flex flex-col justify-center">
          <Link to="/equipment/putters" className="label-caps text-white/70 hover:text-[#E31837] btn-transition inline-flex items-center gap-1 w-fit" data-testid="back-to-putters">
            <ChevronLeft className="h-4 w-4" /> All Putter Families
          </Link>
          <h1 className="heading-1 text-white mt-3 drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]" data-testid="putter-sub-title">{sub?.name || "…"}</h1>
        </div>
      </section>

      {/* Description */}
      {sub?.description && (
        <section className="container-ping py-10 max-w-3xl">
          <p className="text-muted-foreground leading-relaxed text-base md:text-lg" data-testid="putter-sub-description">
            {sub.description}
          </p>
        </section>
      )}

      {/* Products */}
      <section className="container-ping">
        <div className="border-t border-border pt-8 mb-6 flex items-baseline justify-between">
          <h2 className="heading-3">Available Models</h2>
          <p className="text-sm text-muted-foreground" data-testid="putter-sub-count">{products.length} models</p>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-square bg-secondary animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground">No models available yet — check back soon.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="putter-sub-products-grid">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
